import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Loader2, AlertTriangle, Database, ArrowRight } from "lucide-react";
import { 
  useCheckStatusKeyUsage, 
  useUpdateStatusKey, 
  type StatusKeyUsage,
  type BookingStatus 
} from "@/hooks/useEventManagement";

interface StatusKeyUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: BookingStatus;
}

export function StatusKeyUpdateDialog({ 
  open, 
  onOpenChange, 
  status 
}: StatusKeyUpdateDialogProps) {
  const [newStatusKey, setNewStatusKey] = useState("");
  const [usage, setUsage] = useState<StatusKeyUsage | null>(null);
  const [step, setStep] = useState<"input" | "confirm">("input");
  
  const checkUsage = useCheckStatusKeyUsage();
  const updateStatusKey = useUpdateStatusKey();

  useEffect(() => {
    if (open && status) {
      setNewStatusKey(status.status_key);
      setUsage(null);
      setStep("input");
    }
  }, [open, status]);

  const handleCheckUsage = async () => {
    if (newStatusKey === status.status_key) {
      // No change needed
      onOpenChange(false);
      return;
    }

    try {
      const usageResult = await checkUsage.mutateAsync(status.status_key);
      setUsage(usageResult);
      setStep("confirm");
    } catch (error) {
      console.error("Failed to check usage:", error);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      await updateStatusKey.mutateAsync({
        statusId: status.id,
        newStatusKey,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update status key:", error);
    }
  };

  const isValidStatusKey = (key: string) => {
    return /^[a-z0-9_]+$/.test(key) && key.length >= 2 && key.length <= 50;
  };

  const canProceed = newStatusKey && 
    newStatusKey !== status.status_key && 
    isValidStatusKey(newStatusKey);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Status Key</DialogTitle>
          <DialogDescription>
            {step === "input" 
              ? "Change the unique identifier for this booking status"
              : "Confirm the changes to be made"
            }
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Status</Label>
              <div className="flex items-center gap-2 p-2 bg-muted rounded">
                <Badge 
                  style={{ 
                    backgroundColor: `${status.color}20`,
                    color: status.color,
                    border: `1px solid ${status.color}40`
                  }}
                >
                  {status.display_name}
                </Badge>
                <span className="font-mono text-sm text-muted-foreground">
                  {status.status_key}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-status-key">New Status Key*</Label>
              <Input
                id="new-status-key"
                value={newStatusKey}
                onChange={(e) => setNewStatusKey(e.target.value.toLowerCase())}
                placeholder="e.g., collection_scheduled"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Only lowercase letters, numbers, and underscores allowed
              </p>
            </div>

            {newStatusKey && !isValidStatusKey(newStatusKey) && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Invalid Status Key</AlertTitle>
                <AlertDescription>
                  Status key must contain only lowercase letters, numbers, and underscores,
                  and be between 2-50 characters long.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === "confirm" && usage && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono bg-muted px-2 py-1 rounded">
                {status.status_key}
              </span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-mono bg-muted px-2 py-1 rounded">
                {newStatusKey}
              </span>
            </div>

            {usage.in_use && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Status Key In Use</AlertTitle>
                <AlertDescription>
                  This status is currently used by {usage.active_bookings_count} active booking(s).
                  Updating will affect all related records.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <Database className="h-4 w-4" />
              <AlertTitle>Impact Analysis</AlertTitle>
              <AlertDescription>
                This change will update references in:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Booking records</li>
                  <li>Status transitions</li>
                  <li>Event flows</li>
                  <li>Event queue items</li>
                </ul>
              </AlertDescription>
            </Alert>

            <p className="text-sm text-muted-foreground">
              This action cannot be undone. All dependent records will be updated automatically.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={checkUsage.isPending || updateStatusKey.isPending}
          >
            Cancel
          </Button>
          
          {step === "input" && (
            <Button 
              onClick={handleCheckUsage}
              disabled={!canProceed || checkUsage.isPending}
            >
              {checkUsage.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Check Impact
            </Button>
          )}
          
          {step === "confirm" && (
            <Button 
              onClick={handleConfirmUpdate}
              disabled={updateStatusKey.isPending}
              variant={usage?.in_use ? "destructive" : "default"}
            >
              {updateStatusKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Status Key
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}